import { useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { usePermissionsContext } from 'modules/Common/permissions/PermissionsProvider';

// Exported so tests build the real key rather than guessing at it — an earlier
// revision of this file renamed the constant, the test kept importing the old
// name, and the resulting `undefined` key meant the guard was never actually
// under test.
export const WHATS_NEW_SESSION_KEY_PREFIX = 'cg.whatsNew.autoOpened';

// Routes where an unrequested announcement would be an interruption.
//
// This gates AUTO-OPENING ONLY — it does not close a drawer the user already
// has open, and it does not unmount one. Navigating from /dashboard into the
// editor with the drawer up leaves it up (non-modal, so the canvas stays
// usable); tearing it down mid-read would also skip the read-marking that
// closing performs, leaving the notes unread and the badge lit.
//
// `/trial-expired` is deliberately in-layout (_app.jsx:126-131), so without it
// listed here a user whose trial has just lapsed is greeted with product news.
// The diagram editor owns the full viewport with its own ribbon — OI-6, a
// product call, and one line to reverse.
const SUPPRESSED_ROUTE_PATTERNS = ['/trial-expired', '/new-diagram', '/diagrams/:id'];

// The guard is scoped to the SIGNED-IN USER, not just the tab.
//
// sessionStorage alone was wrong: sign-out → sign-in is a pure SPA transition
// (_app.jsx:304-313 clears the token and the query cache and nothing else), so
// a bare key set for user A survives into user B's session in the same tab and
// suppresses the drawer for them permanently. Appending the Clerk user id
// scopes it to "this login in this tab", which is what the feature actually
// means.
//
// Still sessionStorage rather than localStorage: it must not persist past the
// tab, or the drawer would be suppressed on the user's NEXT login — exactly
// when it should appear. And not a React ref, because everything under
// AppLayout sits inside OrgBoundary's `key={switchCount}` (OrgBoundary.jsx:373)
// and an org switch remounts the tree, resetting a ref.
const sessionKeyFor = (userId) => `${WHATS_NEW_SESSION_KEY_PREFIX}.${userId ?? 'anon'}`;

const alreadyOpenedThisSession = (userId) => {
  try {
    return sessionStorage.getItem(sessionKeyFor(userId)) === '1';
  } catch {
    // Private mode / blocked site data. Failing closed (treating it as already
    // opened) would hide the feature entirely for those users; failing open
    // shows it once per mount, which is the milder wrong.
    return false;
  }
};

const rememberOpenedThisSession = (userId) => {
  try {
    sessionStorage.setItem(sessionKeyFor(userId), '1');
  } catch {
    /* storage unavailable — the drawer may reappear on remount, which is fine */
  }
};

/**
 * Decides whether the post-login drawer opens (D5).
 *
 * Returns `[open, setOpen]` so the caller keeps ordinary control of the drawer
 * once it is on screen; this hook only ever opens it, never closes it.
 */
export const useWhatsNewAutoOpen = (unreadCount) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { status } = usePermissionsContext() ?? {};
  const { user } = useUser() ?? {};
  const userId = user?.id;

  const suppressed = SUPPRESSED_ROUTE_PATTERNS.some((pattern) =>
    matchPath(location.pathname, { path: pattern, exact: false }),
  );

  useEffect(() => {
    // `status === 'ready'` is the gate that matters most. PermissionsProvider
    // holds a 30s provisioning grace window (PROVISIONING_GRACE_MS) during
    // which a freshly-provisioned magic-link user reports 'loading' and looks
    // permission-less. Opening on user presence alone would put the drawer over
    // a workspace that has not been judged yet, while WorkspacePlanBoundary may
    // still swap the page beneath it for a loader or an error state.
    if (status !== 'ready') return;
    if (suppressed) return;
    if (unreadCount === 0) return;
    if (alreadyOpenedThisSession(userId)) return;

    rememberOpenedThisSession(userId);
    setOpen(true);
  }, [status, suppressed, unreadCount, userId]);

  return [open, setOpen];
};

export default useWhatsNewAutoOpen;
